import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"
import { projectPolygonToLocalMetres } from "../editor/projection"

// Handles the cadastre-number form: POSTs to /plots/fetch, on success pushes
// the polygon (converted to local metres) into the editor store, which causes
// the editor to re-fit and re-render.
export default class extends Controller {
  static targets = ["input", "status", "meta", "button"]

  async submit(event) {
    event.preventDefault()
    const number = this.inputTarget.value.trim()
    if (!number) return

    this.#setBusy(true)
    this.#setStatus("loading", "Загружаю данные из Росреестра…")
    this.#renderMeta(null)

    try {
      const body = await this.#postFetch(number)
      if (body._failed) {
        this.#setStatus("error", body.message)
        return
      }

      const ringLngLat = extractOuterRing(body.polygon)
      if (!ringLngLat || ringLngLat.length < 3) {
        this.#setStatus("error", "Не удалось распарсить границы участка")
        return
      }

      const { polygonM, originLngLat } = projectPolygonToLocalMetres(ringLngLat)
      editorStore.replacePlot({
        source: "cadastre",
        cadastreNumber: body.cadastre_number,
        polygonM,
        originLngLat,
        address: body.address,
        area_m2: body.area_m2,
        vri: body.vri,
      })

      this.#setStatus("ok", "Участок загружен")
      this.#renderMeta(body)
    } catch (err) {
      this.#setStatus("error", `Сеть: ${err.message}`)
    } finally {
      this.#setBusy(false)
    }
  }

  async #postFetch(number) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch("/plots/fetch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token ?? "",
      },
      body: JSON.stringify({ number }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      return { _failed: true, message: body.message || `Ошибка ${response.status}` }
    }
    return body
  }

  #setBusy(busy) {
    const btn = this.hasButtonTarget ? this.buttonTarget : this.element.querySelector('button[type="submit"]')
    if (!btn) return
    btn.disabled = busy
    btn.setAttribute("aria-busy", busy ? "true" : "false")
    btn.classList.toggle("opacity-60", busy)
    btn.classList.toggle("cursor-not-allowed", busy)
  }

  #setStatus(level, text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.dataset.level = level
    this.statusTarget.textContent = text
  }

  #renderMeta(body) {
    if (!this.hasMetaTarget) return
    if (!body) { this.metaTarget.textContent = ""; return }
    const bits = [body.address, body.vri, body.area_m2 ? `${body.area_m2} м²` : null].filter(Boolean)
    this.metaTarget.textContent = bits.join(" · ")
  }
}

function extractOuterRing(geojson) {
  if (!geojson) return null
  if (geojson.type === "Polygon") return geojson.coordinates?.[0]
  if (geojson.type === "MultiPolygon") return geojson.coordinates?.[0]?.[0]
  return null
}
